import React from "react";
import { SeoAnalysisReport } from "../seoAnalyzer";
import { Search, Zap, Accessibility, Shield, Share2, Award, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

interface ScoreGaugesProps {
  report: SeoAnalysisReport;
}

export const ScoreGauges: React.FC<ScoreGaugesProps> = ({ report }) => {
  const { overall, seo, performance, accessibility, security, social } = report.scores;

  const getScoreColor = (val: number) => {
    if (val >= 90) return "var(--neon-emerald)";
    if (val >= 70) return "var(--neon-amber)";
    return "var(--neon-crimson)";
  };

  const getStatusBadge = (val: number) => {
    if (val >= 90) {
      return (
        <div className="status-badge status-badge-emerald">
          <CheckCircle2 size={14} /> SYSTEM OPTIMAL
        </div>
      );
    }
    if (val >= 70) {
      return (
        <div className="status-badge status-badge-amber">
          <AlertTriangle size={14} /> MODERATE WARNINGS
        </div>
      );
    }
    return (
      <div className="status-badge status-badge-crimson">
        <XCircle size={14} /> CRITICAL AUDIT NEEDED
      </div>
    );
  };

  // SVG ring circumference
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (overall / 100) * circumference;

  const categories = [
    {
      id: "seo",
      title: "SEO Health",
      score: seo,
      icon: <Search size={18} color="var(--neon-cyan)" />,
      color: getScoreColor(seo),
      passCount: report.audits.filter((a) => a.category === "seo" && a.status === "pass").length,
      totalCount: report.audits.filter((a) => a.category === "seo").length,
    },
    {
      id: "performance",
      title: "Performance",
      score: performance,
      icon: <Zap size={18} color="var(--neon-amber)" />,
      color: getScoreColor(performance),
      passCount: report.audits.filter((a) => a.category === "performance" && a.status === "pass").length,
      totalCount: report.audits.filter((a) => a.category === "performance").length,
    },
    {
      id: "accessibility",
      title: "Accessibility",
      score: accessibility,
      icon: <Accessibility size={18} color="var(--neon-emerald)" />,
      color: getScoreColor(accessibility),
      passCount: report.audits.filter((a) => a.category === "accessibility" && a.status === "pass").length,
      totalCount: report.audits.filter((a) => a.category === "accessibility").length,
    },
    {
      id: "security",
      title: "Security & Headers",
      score: security,
      icon: <Shield size={18} color="var(--neon-purple)" />,
      color: getScoreColor(security),
      passCount: report.audits.filter((a) => a.category === "security" && a.status === "pass").length,
      totalCount: report.audits.filter((a) => a.category === "security").length,
    },
    {
      id: "social",
      title: "Social Graph",
      score: social,
      icon: <Share2 size={18} color="var(--neon-blue)" />,
      color: getScoreColor(social),
      passCount: report.audits.filter((a) => a.category === "social" && a.status === "pass").length,
      totalCount: report.audits.filter((a) => a.category === "social").length,
    },
  ];

  return (
    <div className="scores-grid">
      {/* Main Overall Score Card */}
      <div className="cyber-card overall-score-card">
        <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Award size={16} color="var(--neon-cyan)" /> OVERALL INTEL RATING
        </div>

        <div className="score-ring-wrapper">
          <svg className="score-svg" viewBox="0 0 160 160">
            <circle className="score-bg-circle" cx="80" cy="80" r={radius} />
            <circle
              className="score-progress-circle"
              cx="80"
              cy="80"
              r={radius}
              stroke={getScoreColor(overall)}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
            />
          </svg>
          <div className="score-number-overlay">
            <span className="score-val" style={{ color: getScoreColor(overall) }}>
              {overall}
            </span>
            <span className="score-max">/ 100</span>
          </div>
        </div>

        {getStatusBadge(overall)}
      </div>

      {/* Subcategory Score Cards */}
      <div className="category-scores-list">
        {categories.map((cat) => (
          <div key={cat.id} className="cyber-card cat-card">
            <div>
              <div className="cat-header">
                <div className="cat-icon">{cat.icon}</div>
                <div className="cat-score" style={{ color: cat.color }}>
                  {cat.score}
                </div>
              </div>
              <div className="cat-title">{cat.title}</div>
            </div>

            <div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontFamily: "var(--font-mono)", display: "flex", justifyContent: "space-between" }}>
                <span>Passed Audits</span>
                <span>{cat.passCount} / {cat.totalCount}</span>
              </div>
              <div className="mini-bar">
                <div
                  className="mini-bar-fill"
                  style={{
                    width: `${cat.score}%`,
                    backgroundColor: cat.color,
                    boxShadow: `0 0 10px ${cat.color}`,
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
